import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {BehaviorSubject, Observable} from 'rxjs';
import {MeasureMetricService} from '@theme/components/card-components/spatial-card/components/measure-metric/measure-metric.service';
import {MetricDto, MetricGroupDto} from '../../../../../../pages/geospatial-viewer/layers/metric-layer';

@Component({
    selector: 'ngx-spatial-card-metric-select',
    templateUrl: './spatial-card-metric-select.component.html',
    styleUrls: ['./spatial-card-metric-select.component.scss'],
    providers: [MeasureMetricService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpatialCardMetricSelectComponent extends Unsubscribable implements OnInit {
    @Input() options: MetricGroupDto[] = [];
    @Input() initMetricKey: string = null;
    @Output() onSelectChange: EventEmitter<{measure: MetricGroupDto; metric: MetricDto}> = new EventEmitter();

    measureItems: MetricGroupDto[] = [];
    metricItems: MetricDto[] = [];
    private activeMeasure: BehaviorSubject<MetricGroupDto> = new BehaviorSubject<MetricGroupDto>(null);
    private activeMetric: BehaviorSubject<MetricDto> = new BehaviorSubject<MetricDto>(null);
    readonly activeMeasure$: Observable<MetricGroupDto> = this.activeMeasure.asObservable();
    readonly activeMetric$: Observable<MetricDto> = this.activeMetric.asObservable();

    constructor(private measureMetricService: MeasureMetricService) {
        super();
    }

    ngOnInit(): void {
        this.measureItems = this.options;

        // Set active measure and metric, based on initMetric (if available) else default
        let activeMeasure: MetricGroupDto;
        let activeMetric: MetricDto;
        if (this.initMetricKey) {
            activeMeasure = this.measureMetricService.getMeasureByMetricKey(this.initMetricKey, this.options);
            activeMetric = this.measureMetricService.getMetricFromMetricKey(this.initMetricKey, this.options);
        } else {
            activeMeasure = this.measureMetricService.getDefaultMeasure(this.options);
            activeMetric = this.measureMetricService.getDefaultMetric(activeMeasure);
        }

        if (this.activeMeasure.value === null) {
            this.metricItems = activeMeasure.metrics;
            this.activeMeasure.next(activeMeasure);
            this.activeMetric.next(activeMetric);
        }
    }

    // Update selected measure, if the new value is different from the current value. Clear selected metric.
    public onMeasureSelect(measureCode: string): void {
        if (measureCode && this.activeMeasure.value.key !== measureCode) {
            const fullMeasure: MetricGroupDto = this.measureItems.filter((m) => m.key === measureCode)[0];
            this.updateMeasure(fullMeasure);
            this.activeMetric.next(null);
            this.metricItems = this.activeMeasure.value.metrics;
        }
    }

    // Update selected metric, if the new value is different from the current value
    public onMetricSelect(metricCode: string): void {
        if (metricCode && (this.activeMetric.value == null || this.activeMetric.value.key !== metricCode)) {
            const activeMeasure: MetricGroupDto = this.measureItems.filter(
                (m) => m.key === this.activeMeasure.value.key,
            )[0];
            const fullMetric: MetricDto = activeMeasure.metrics.filter((m) => m.key === metricCode)[0];
            this.updateMetric(fullMetric);
            this.onSelectChange.emit({measure: this.activeMeasure.value, metric: this.activeMetric.value});
        }
    }

    public getMeasureLabel(measureKey: string): string {
        return this.measureMetricService.getMeasureByMeasureKey(measureKey, this.options).label;
    }

    public getMetricLabel(metricKey: string): string {
        return this.measureMetricService.getMetricFromMetricKey(metricKey, this.options).label;
    }

    // Update selected measure, and reset selected metric to nothing
    public updateMeasure(newMeasure: MetricGroupDto) {
        if (newMeasure && this.activeMeasure.value.key !== newMeasure.key) {
            this.activeMetric.next(null); // force null
            this.activeMeasure.next(newMeasure);
        }
    }

    public updateMetric(newMetric: MetricDto) {
        if (newMetric && (this.activeMetric.value == null || this.activeMetric.value.key !== newMetric.key)) {
            this.activeMetric.next(newMetric);
        }
    }
}
